/**
 * Wire event payloads, **derived** from the engine's own event types.
 *
 * The control channel carries `EVENT.payload.data` as the engine's event data
 * with every scaled integer encoded as a canonical decimal string
 * (`toWireJson`, docs/10 §4). There is no second hand-written copy of those
 * shapes in this app: `Wire<T>` maps `bigint → string` recursively over the
 * engine's `EventDataMap`, so a field added or renamed in `engine/core` breaks
 * this file at compile time instead of silently rendering `undefined`.
 *
 * The import is type-only (`verbatimModuleSyntax`), so nothing from `engine/`
 * is bundled into the browser: the module is erased at build time.
 */

import type { EventDataMap } from '../../../../engine/core/types.js';
import type { EventPayload } from '@weke/protocol';

/** `bigint` → canonical decimal string; everything else keeps its shape. */
export type Wire<T> = T extends bigint
  ? string
  : T extends string | number | boolean | null | undefined
    ? T
    : T extends readonly (infer U)[]
      ? Wire<U>[]
      : T extends object
        ? { [K in keyof T]: Wire<T[K]> }
        : T;

export type WireEventData = { [K in keyof EventDataMap]: Wire<EventDataMap[K]> };

export type WireEvent = {
  [K in keyof WireEventData]: {
    readonly type: K;
    readonly seq: string;
    readonly at: string;
    readonly data: WireEventData[K];
  };
}[keyof WireEventData];

/**
 * Narrow a validated `EVENT` frame to its typed wire event.
 *
 * The envelope schema validates `type` against the engine's event vocabulary and
 * leaves `data` as `unknown` (it is the engine's payload, not the envelope's
 * business). This cast is the single place the app asserts the payload matches
 * its type — the same assumption `services/trade` makes when it encodes it.
 */
export function asWireEvent(payload: EventPayload): WireEvent {
  return {
    type: payload.type,
    seq: payload.seq,
    at: payload.at,
    data: payload.data,
  } as WireEvent;
}
